import React from 'react';
import { Shield, User, MessageSquare, CreditCard, Phone, CheckCircle } from 'lucide-react';

// Ensure React is available
const ReactMemo = React?.memo || ((component: any) => component);

const cardClass = 'home-surface-interactive p-6 rounded-2xl';
const iconClass =
  'w-12 h-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4';

const WhyChooseSection = ReactMemo(() => {
  return (
    <section
      className="bg-neutral-50 py-20 sm:py-24"
      aria-labelledby="why-choose-heading"
      role="region"
    >
      <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            Why choose us
          </p>
          <h2
            id="why-choose-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
          >
            Why renters choose our concierge
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            A premium, human‑first rental experience with verified homes, trusted agents, and end‑to‑end support.
          </p>
        </header>

        <ul
          className="mb-12 flex w-full list-none flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-2xl border border-neutral-200/80 bg-white px-6 py-4 shadow-sm sm:px-8 sm:py-5"
          aria-label="Trust metrics"
        >
          <li className="text-sm font-medium text-neutral-900">1,200+ renters matched</li>
          <li className="text-sm font-medium text-neutral-900 before:mr-4 before:inline-block before:text-neutral-500 before:content-['·'] sm:before:mr-6">
            &lt;24h average response
          </li>
          <li className="text-sm font-medium text-neutral-900 before:mr-4 before:inline-block before:text-neutral-500 before:content-['·'] sm:before:mr-6">
            Verified listings only
          </li>
        </ul>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          role="list"
          aria-label="Platform features and benefits"
        >
          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <Shield className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">Verified Properties</h3>
            <p className="text-neutral-600">
              Every listing is verified by our team to ensure quality and accuracy
            </p>
          </article>

          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <User className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">Trusted Agents</h3>
            <p className="text-neutral-600">
              Work with licensed and verified real estate professionals
            </p>
          </article>

          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <MessageSquare className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">Direct Communication</h3>
            <p className="text-neutral-600">
              Built-in messaging system for secure communication
            </p>
          </article>

          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <CreditCard className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">Secure Payments</h3>
            <p className="text-neutral-600">
              Multiple payment options with bank-level security
            </p>
          </article>

          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <Phone className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">24/7 Support</h3>
            <p className="text-neutral-600">
              Get help anytime with our dedicated support team
            </p>
          </article>

          <article className={cardClass} role="listitem">
            <div className={iconClass} aria-hidden="true">
              <CheckCircle className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-neutral-800">Complete Solution</h3>
            <p className="text-neutral-600">
              End-to-end rental management from search to payment
            </p>
          </article>
        </div>
      </div>
    </section>
  );
});

WhyChooseSection.displayName = 'WhyChooseSection';

export default WhyChooseSection;